import { useMemo } from "react"

import merge from "lodash-es/merge"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { ProductPosition } from "shared/types/ProductPosition"
import { ProductPositionTypename } from "shared/types/Project"

import { useProductPositionMutations } from "entities/product-positions/api/useProductPositionMutations"
import { ProductPositionFormData } from "entities/product-positions/types/ProductPositionForm"
import { useDefaultProjectChildPosition } from "entities/projects/model/useDefaultProjectChildPosition"
import { useProductPositionFormModel } from "widgets/product-position/product-position-form/model/useProductPositionFormModel"

import { NewProductPositionOverlayModel } from "../NewProductPositionOverlay"
import { NewProductPositionOverlayPayload } from "../NewProductPositionOverlayConnector"

const defaultProductPosition: Partial<ProductPosition> = {
  quantity: 1,
  position: 0
}

interface NewProductPositionOverlayModelArgs {
  payload: NewProductPositionOverlayPayload
}

export function useNewProductPositionOverlayModel({
  payload: {
    projectID,
    hideProject,
    excludedProjectIDs,
    defaultValues,
    title,
    submitButtonText
  }
}: NewProductPositionOverlayModelArgs): NewProductPositionOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { createProductPosition } = useProductPositionMutations()

  const { defaultParentID, defaultPosition } = useDefaultProjectChildPosition({
    projectID,
    childType: ProductPositionTypename
  })

  const submitProductPosition = useEvent(
    (formData: ProductPositionFormData) => {
      if (!formData.productPosition?.variantKey?.trim()) {
        return Promise.resolve({ data: null, errors: [{ message: "invalid" }] })
      }
      return createProductPosition({
        projectID: formData.projectID || projectID,
        parentID: formData.parentID || undefined,
        displayPosition: formData.displayPosition,
        productPosition: {
          ...formData.productPosition,
          price:
            formData.productPosition.price !== undefined
              ? formData.productPosition.price
              : defaultValues?.price,
          priceGroup:
            formData.productPosition.priceGroup !== undefined
              ? formData.productPosition.priceGroup
              : defaultValues?.priceGroup,
          id: generateUUID()
        }
      })
    }
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<ProductPositionFormData>(
    {
      onSubmit: submitProductPosition,
      onSuccess: closeOverlay
    }
  )

  const mergedDefaults = useMemo(
    () =>
      merge({}, defaultProductPosition, defaultValues, {
        position: defaultPosition
      }),
    [defaultValues, defaultPosition, defaultProductPosition]
  )

  const productPositionFormModel = useProductPositionFormModel({
    id: "new-product-position-overlay-form",
    defaultProjectID: projectID,
    defaultParentID,
    excludedProjectIDs,
    productPosition: mergedDefaults,
    onSubmit: handleSubmit,
    isDefaultEditing: true,
    // Editing is controlled to `true`: this overlay closes on success via
    // useFormSubmit, so the form's own finishEditing() gate must never flip it
    // read-only (which would otherwise freeze the inputs when a submit fails).
    isEditing: true,
    ensureEditableProject: !hideProject,
    formOptions: {
      hideProject,
      hideClarificationRequest: true,
      hideAttachments: true,
      hidePrice: true
    }
  })

  return {
    onClose: closeOverlay,
    productPositionFormModel,
    title,
    submitButtonText,
    isLoading: isSubmitting,
    isSubmitting
  }
}
